const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async handleResponse(response) {
    console.log('Handling response:', response.status, response.statusText);
    
    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (e) {
        console.error('Failed to parse error response:', e);
      }

      const errorCode = response.headers.get('X-Error-Code');
      if (errorCode === '2FA_EXPIRED') {
        const error = new Error(errorData.detail || '2FA expired, please login again');
        error.code = '2FA_EXPIRED';
        throw error;
      }
      
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    
    try {
      return await response.json();
    } catch (e) {
      console.error('Failed to parse response:', e);
      throw new Error('Invalid response format');
    }
  }

  createFormData(data) {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key]);
      }
    });
    return formData;
  }

  async isLoggedIn() {
    try {
      const response = await fetch(`${this.baseURL}/api/auth/me`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        return { isLoggedIn: true, user: data.user };
      } else {
        return { isLoggedIn: false, user: null };
      }
    } catch (error) {
      return { isLoggedIn: false, user: null };
    }
  }

  async login(email, password, eula = false) {
    try {
      const formData = this.createFormData({
        email,
        password,
        eula
      });

      
      const response = await fetch(`${this.baseURL}/api/auth/login`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Login fetch error:', error);
      throw error;
    }
  }

  async verify2FA(code, skip2FA = false) {
    const formData = this.createFormData({ code, skip_2fa: skip2FA });

    const response = await fetch(`${this.baseURL}/api/auth/2fa`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async resend2FA() {
    const response = await fetch(`${this.baseURL}/api/auth/2fa/resend`, {
      method: 'POST',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async register(email, password, confirmPassword, eula = false) {
    const formData = this.createFormData({
      email,
      password,
      confirm_password: confirmPassword,
      eula
    });

    const response = await fetch(`${this.baseURL}/api/auth/register`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async logout() {
    const response = await fetch(`${this.baseURL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async revokeTrustedDevice() {
    const response = await fetch(`${this.baseURL}/api/auth/revoke-trusted-device`, {
      method: 'POST',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async revokeAllTrustedDevices() {
    const response = await fetch(`${this.baseURL}/api/auth/revoke-all-trusted-devices`, {
      method: 'POST',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async changePassword(currentPassword, newPassword, confirmPassword) {
    const formData = this.createFormData({
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword
    });

    const response = await fetch(`${this.baseURL}/api/auth/change-password`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async getCurrentUser() {
    const response = await fetch(`${this.baseURL}/api/auth/me`, {
      method: 'POST',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async getDashboard() {
    const response = await fetch(`${this.baseURL}/api/dashboard`, {
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async getDomains() {
    const response = await fetch(`${this.baseURL}/api/domains`, {
      method: 'GET',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async getUrls() {
    const response = await fetch(`${this.baseURL}/api/urls`, {
      method: 'GET',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async createDomain(name, frequency = 24) {
    const formData = this.createFormData({ name, frequency });

    const response = await fetch(`${this.baseURL}/api/domains`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async deleteDomain(name) {
    const response = await fetch(`${this.baseURL}/api/domains/${name}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async getDomainDetails(domain) {
    const response = await fetch(`${this.baseURL}/api/domains/${domain}`, {
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async getSearchStatus(domain) {
    const response = await fetch(`${this.baseURL}/api/domains/${domain}/search/status`, {
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async searchDomainNow(domain) {
    const response = await fetch(`${this.baseURL}/api/domains/${domain}/search`, {
      method: 'POST',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async getTags() {
    const response = await fetch(`${this.baseURL}/api/tags`, {
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async createTag(tagData) {
    const formData = this.createFormData(tagData);
    const response = await fetch(`${this.baseURL}/api/tags`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async deleteTag(tagId) {
    const response = await fetch(`${this.baseURL}/api/tags/${tagId}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    return this.handleResponse(response);
  }

  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/api/health`);
      return this.handleResponse(response);
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  async getAllUsers() {
    const response = await fetch(`${this.baseURL}/api/admin`, {
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async searchHistory(domain) {
    const formData = this.createFormData({ domain });
    const response = await fetch(`${this.baseURL}/api/admin/search`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async searchUrlHistory(url) {
    const formData = this.createFormData({ url });
    const response = await fetch(`${this.baseURL}/api/admin/search/url`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async searchSlugHistory(slug) {
    const formData = this.createFormData({ slug });
    const response = await fetch(`${this.baseURL}/api/admin/search/slug`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async getIntelxStatus() {
    const response = await fetch(`${this.baseURL}/api/admin/intelx`, {
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async updateIntelxToggle(name, enabled) {
    const response = await fetch(`${this.baseURL}/api/admin/intelx/toggles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ name, enabled }),
    });
    return this.handleResponse(response);
  }

  async updateIntelxSetting(name, value) {
    const response = await fetch(`${this.baseURL}/api/admin/intelx/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ name, value }),
    });
    return this.handleResponse(response);
  }

  async getActiveSearches() {
    const response = await fetch(`${this.baseURL}/api/admin/active-searches`, {
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async createUser(email, password, confirmPassword, userType = 1) {
    const formData = this.createFormData({ email, password, confirm_password: confirmPassword, user_type: userType });
    const response = await fetch(`${this.baseURL}/api/admin/users`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async deleteUser(userId) {
    const response = await fetch(`${this.baseURL}/api/admin/users/${userId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async updateUser(userId, password, confirmPassword, userType = 1) {
    const data = { user_type: userType };
    if (password) data.password = password;
    if (confirmPassword) data.confirm_password = confirmPassword;
    const formData = this.createFormData(data);
    const response = await fetch(`${this.baseURL}/api/admin/users/${userId}`, {
      method: 'PUT',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async updateUserCredentialVisibility(userId, credentialVisibility) {
    const formData = this.createFormData({ credential_visibility: credentialVisibility });
    const response = await fetch(`${this.baseURL}/api/admin/users/${userId}/credential-visibility`, {
      method: 'PUT',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async getDomainsByUser(userId) {
    const response = await fetch(`${this.baseURL}/api/admin/domains/${userId}`, {
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async createDomainForUser(userId, name, frequency = 24, type = 1, searchType = "weekly") {
    const formData = this.createFormData({ name, frequency, type, search_type: searchType });
    const response = await fetch(`${this.baseURL}/api/admin/domains/${userId}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async deleteDomainForUser(userId, domainName) {
    const response = await fetch(`${this.baseURL}/api/admin/domains/${userId}/${domainName}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async getUrlsByUser(userId) {
    const response = await fetch(`${this.baseURL}/api/admin/urls/${userId}`, {
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async createUrlForUser(userId, name, frequency = 24, type = 1, searchType = "weekly") {
    const formData = this.createFormData({ name, frequency, type, search_type: searchType });
    const response = await fetch(`${this.baseURL}/api/admin/urls/${userId}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async deleteUrlForUser(userId, urlName) {
    const response = await fetch(`${this.baseURL}/api/admin/urls/${userId}/${urlName}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async getSlugs() {
    const response = await fetch(`${this.baseURL}/api/slugs`, {
      method: 'GET',
      credentials: 'include'
    });
    return this.handleResponse(response);
  }

  async getSlugDetails(slug) {
    const response = await fetch(`${this.baseURL}/api/slugs/${encodeURIComponent(slug)}`, {
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async downloadImportTemplate() {
    const response = await fetch(`${this.baseURL}/api/admin/import/template`, {
      credentials: 'include',
    });
    if (!response.ok) {
      return this.handleResponse(response);
    }
    return response.blob();
  }

  // The file goes as the raw body, so the server reads it in memory instead of spooling a multipart upload to disk
  async importForUser(userId, file) {
    const response = await fetch(`${this.baseURL}/api/admin/import/${userId}`, {
      method: 'POST',
      body: file,
      credentials: 'include',
      headers: { 'Content-Type': 'text/csv' },
    });
    return this.handleResponse(response);
  }

  async getSlugsByUser(userId) {
    const response = await fetch(`${this.baseURL}/api/admin/slugs/${userId}`, {
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async createSlugForUser(userId, name) {
    const formData = this.createFormData({ name });
    const response = await fetch(`${this.baseURL}/api/admin/slugs/${userId}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async deleteSlugForUser(userId, slugName) {
    const response = await fetch(`${this.baseURL}/api/admin/slugs/${userId}/${encodeURIComponent(slugName)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async createUrl(name, frequency = 24) {
    const formData = this.createFormData({ name, frequency });
    const response = await fetch(`${this.baseURL}/api/urls`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async deleteUrl(name) {
    const response = await fetch(`${this.baseURL}/api/urls/${encodeURIComponent(name)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async getUrlDetails(url) {
    const response = await fetch(`${this.baseURL}/api/urls/${encodeURIComponent(url)}`, {
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async getUrlSearchStatus(url) {
    const response = await fetch(`${this.baseURL}/api/urls/${encodeURIComponent(url)}/search/status`, {
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async searchUrlNow(url) {
    const response = await fetch(`${this.baseURL}/api/urls/${encodeURIComponent(url)}/search`, {
      method: 'POST',
      credentials: 'include',
    });
    return this.handleResponse(response);
  }

  async updateSolvedBatch(updates) {
    const response = await fetch(`${this.baseURL}/api/updateSolvedBatch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ updates }),
    });
    return this.handleResponse(response);
  }

  async updateRiskScoreBatch(updates) {
    const response = await fetch(`${this.baseURL}/api/updateRiskScore`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ updates }),
    });
    return this.handleResponse(response);
  }

  async updateComputerTypeBatch(updates) {
    const response = await fetch(`${this.baseURL}/api/updateComputerType`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ updates }),
    });
    return this.handleResponse(response);
  }

  async addTagToRecord(recordId, tagId) {
    const response = await fetch(`${this.baseURL}/api/records/${recordId}/tags/${tagId}`, {
      method: 'POST',
      credentials: 'include'
    });
    return this.handleResponse(response);
  }

  async removeTagFromRecord(recordId, tagId) {
    const response = await fetch(`${this.baseURL}/api/records/${recordId}/tags/${tagId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    return this.handleResponse(response);
  }
}

const api = new ApiService();
export default api;
